import { useParams, Link } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import { db } from "../firebase";
import { doc, getDoc } from "firebase/firestore";
import "../styles/ProductDetail.css";

import { addToCart } from "../utils/cart";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faShoppingCart, faChevronLeft, faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { faTruck, faBan, faClock } from "@fortawesome/free-solid-svg-icons";

const ZOOM_LEVEL = 2.0; // How much to magnify

export default function ProductDetail() {
  const { id } = useParams();

  const [product, setProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [imageLoaded, setImageLoaded] = useState(false);

  // 🔍 ZOOM STATE
  const [isZooming, setIsZooming] = useState(false);
  const [zoomPos, setZoomPos] = useState({ x: 0, y: 0 });       // % position on image
  const [lensPos, setLensPos] = useState({ x: 0, y: 0 });       // px position of lens
  const imageWrapperRef = useRef(null);

  const navigate = useNavigate();

  // 🔥 FETCH PRODUCT FROM FIRESTORE
  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const docRef = doc(db, "products", id);
        const snapshot = await getDoc(docRef);

        if (snapshot.exists()) {
          setProduct({
            id: snapshot.id,
            ...snapshot.data(),
          });
        }
      } catch (error) {
        console.error("Error fetching product:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProduct();
  }, [id]);

  // HANDLE QUANTITY CHANGE
  const handleQtyChange = (value) => {
    const newQty = quantity + value;
    if (newQty >= 1 && newQty <= 100) {
      setQuantity(newQty);
    }
  };

  // 🖼️ GALLERY HELPERS
  const getImages = () => {
    if (!product) return [];
    if (product.images && product.images.length > 0) return product.images;
    if (product.image) return [product.image];
    return [];
  };

  const handlePrevImage = () => {
    const imgs = getImages();
    setActiveImageIndex((prev) => (prev === 0 ? imgs.length - 1 : prev - 1));
    setImageLoaded(false);
  };

  const handleNextImage = () => {
    const imgs = getImages();
    setActiveImageIndex((prev) => (prev === imgs.length - 1 ? 0 : prev + 1));
    setImageLoaded(false);
  };

  const handleThumbnailClick = (index) => {
    setActiveImageIndex(index);
    setImageLoaded(false);
  };

  // 🔍 ZOOM HANDLERS
  const handleMouseMove = (e) => {
    const wrapper = imageWrapperRef.current;
    if (!wrapper || !imageLoaded) return;

    const rect = wrapper.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const xPct = Math.min(Math.max(x / rect.width, 0), 1);
    const yPct = Math.min(Math.max(y / rect.height, 0), 1);

    // Lens size
    const lensW = rect.width / ZOOM_LEVEL;
    const lensH = rect.height / ZOOM_LEVEL;

    // Clamp lens so it stays inside the image
    const lensX = Math.min(Math.max(x - lensW / 2, 0), rect.width - lensW);
    const lensY = Math.min(Math.max(y - lensH / 2, 0), rect.height - lensH);

    setLensPos({ x: lensX, y: lensY });
    setZoomPos({ x: xPct * 100, y: yPct * 100 });
  };

  const handleMouseEnter = () => {
    if (imageLoaded) setIsZooming(true);
  };

  const handleMouseLeave = () => {
    setIsZooming(false);
  };

  // 📦 LOADING STATE
  if (isLoading) {
    return (
      <div className="product-detail__loading-container">
        <div className="product-detail__spinner"></div>
        <p className="product-detail__loading-text">Loading product details...</p>
      </div>
    );
  }

  // ❌ NOT FOUND STATE
  if (!product) {
    return (
      <div className="product-detail__error-container">
        <div className="product-detail__error-box">
          <h2 className="product-detail__error-title">Product Not Found</h2>
          <p className="product-detail__error-message">
            The product you're looking for doesn't exist or has been removed.
          </p>
          <Link to="/" className="product-detail__back-btn">
            ← Back to Home
          </Link>
        </div>
      </div>
    );
  }

  const allImages = getImages();
  const activeImage = allImages[activeImageIndex] || "";
  const baseShipping = product.shipping || 0;

  // 🔥 SHIPPING LOGIC
  let shippingCost = baseShipping;
  if (quantity >= 2 && quantity <= 5) {
    shippingCost = baseShipping * 1.5;
  } else if (quantity > 5) {
    shippingCost = baseShipping * 2;
  }

  const totalPrice = product.price * quantity + shippingCost;

  const discountPercent =
    product.originalPrice &&
      product.originalPrice > product.price
      ? Math.round(
        ((product.originalPrice - product.price) /
          product.originalPrice) *
        100
      )
      : 0;

  return (
    <div className="product-detail__wrapper">
      <div className="product-detail__container">
        {/* BREADCRUMB */}
        <div className="product-detail__breadcrumb">
          <Link to="/" className="product-detail__breadcrumb-link">Home</Link>
          <span className="product-detail__breadcrumb-separator">/</span>
          <span className="product-detail__breadcrumb-current">{product.name}</span>
        </div>

        {/* MAIN CONTENT */}
        <div className="product-detail__card">

          {/* IMAGE SECTION */}
          <div className="product-detail__image-section">

            {/* ZOOM WRAPPER — contains main image + zoom panel side by side */}
            <div className="product-detail__zoom-layout">

              {/* MAIN IMAGE WITH ARROWS + LENS */}
              <div
                className="product-detail__main-image-wrapper"
                ref={imageWrapperRef}
                onMouseMove={handleMouseMove}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
              >
                {!imageLoaded && (
                  <div className="product-detail__image-skeleton"></div>
                )}
                <img
                  key={activeImage}
                  src={activeImage}
                  alt={product.name || "product"}
                  className={`product-detail__image ${imageLoaded ? "product-detail__image--loaded" : ""}`}
                  onLoad={() => setImageLoaded(true)}
                  draggable={false}
                />

                {/* ZOOM LENS OVERLAY */}
                {isZooming && imageLoaded && (
                  <div
                    className="product-detail__zoom-lens"
                    style={{
                      left: `${lensPos.x}px`,
                      top: `${lensPos.y}px`,
                      width: `${100 / ZOOM_LEVEL}%`,
                      height: `${100 / ZOOM_LEVEL}%`,
                    }}
                  />
                )}

                {/* PREV / NEXT arrows */}
                {allImages.length > 1 && (
                  <>
                    <button
                      className="product-detail__arrow product-detail__arrow--left"
                      onClick={handlePrevImage}
                      aria-label="Previous image"
                    >
                      <FontAwesomeIcon icon={faChevronLeft} />
                    </button>
                    <button
                      className="product-detail__arrow product-detail__arrow--right"
                      onClick={handleNextImage}
                      aria-label="Next image"
                    >
                      <FontAwesomeIcon icon={faChevronRight} />
                    </button>

                    {/* IMAGE COUNTER DOTS */}
                    <div className="product-detail__dots">
                      {allImages.map((_, i) => (
                        <span
                          key={i}
                          className={`product-detail__dot ${i === activeImageIndex ? "product-detail__dot--active" : ""}`}
                          onClick={() => handleThumbnailClick(i)}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>

              {/* ZOOMED PANEL — appears to the right when hovering */}
              {isZooming && imageLoaded && (
                <div
                  className="product-detail__zoom-panel"
                  style={{
                    backgroundImage: `url(${activeImage})`,
                    backgroundSize: `${ZOOM_LEVEL * 100}%`,
                    backgroundPosition: `${zoomPos.x}% ${zoomPos.y}%`,
                  }}
                />
              )}
            </div>

            {/* THUMBNAILS */}
            {allImages.length > 1 && (
              <div className="product-detail__thumbnails">
                {allImages.map((src, index) => (
                  <div
                    key={index}
                    className={`product-detail__thumbnail ${index === activeImageIndex ? "product-detail__thumbnail--active" : ""}`}
                    onClick={() => handleThumbnailClick(index)}
                  >
                    <img src={src} alt={`thumb-${index}`} />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* INFO SECTION */}
          <div className="product-detail__info-section">
            <h1 className="product-detail__title">{product.name}</h1>

            {/* <div className="product-detail__price-section">
              <p className="product-detail__price">₹{product.price}</p>
              <span className="product-detail__price-badge">Per Unit</span>
            </div> */}

            <div className="product-detail__price-section">

              {product.originalPrice &&
                product.originalPrice > product.price && (
                  <span className="product-detail__original-price">
                    ₹{product.originalPrice}
                  </span>
                )}

              <p className="product-detail__price">
                ₹{product.price}
              </p>

              {discountPercent > 0 && (
                <span className="product-detail__discount">
                  {discountPercent}% OFF
                </span>
              )}

            </div>

            <div className="product-detail__description-box">
              <h3 className="product-detail__description-title">Description</h3>
              {/* <p className="product-detail__description">
                {product.desc || "No description available for this product."}
              </p> */}
              <div
                className="product-detail__description"
                dangerouslySetInnerHTML={{
                  __html: product.desc,
                }}
              />
            </div>

            <div className="product-detail__divider"></div>

            <div className="product-detail__quantity-section">
              <label className="product-detail__quantity-label">Quantity:</label>
              <div className="product-detail__quantity-control">
                <button
                  className="product-detail__qty-btn product-detail__qty-btn--minus"
                  onClick={() => handleQtyChange(-1)}
                  aria-label="Decrease quantity"
                >−</button>
                <input
                  type="number"
                  className="product-detail__qty-input"
                  value={quantity}
                  onChange={(e) => {
                    const val = parseInt(e.target.value);
                    if (val >= 1 && val <= 100) setQuantity(val);
                  }}
                  min="1"
                  max="100"
                  aria-label="Quantity input"
                />
                <button
                  className="product-detail__qty-btn product-detail__qty-btn--plus"
                  onClick={() => handleQtyChange(1)}
                  aria-label="Increase quantity"
                >+</button>
              </div>
            </div>

            <div className="product-detail__total-section">
              <span className="product-detail__total-label">Total Price:</span>
              <div className="product-detail__total-right">
                <span className="product-detail__total-price">₹{totalPrice}</span>
                <span className="product-detail__shipping">
                  Shipping: ₹{Math.round(shippingCost)}
                </span>
              </div>
            </div>

            <div className="product-detail__actions">
              <button
                className="product-detail__order-btn"
                onClick={() => {
                  addToCart({
                    id: product.id,
                    name: product.name,
                    price: product.price,
                    quantity: quantity,
                    image: product.image,
                    shipping: product.shipping,
                  });
                  window.dispatchEvent(new Event("storage"));
                }}
              >
                <FontAwesomeIcon icon={faShoppingCart} /> Add to Cart
              </button>

              <button
                className="product-detail__secondary-btn"
                onClick={() => {
                  navigate("/prebook/" + product.id, {
                    state: {
                      cart: [{
                        id: product.id,
                        name: product.name,
                        price: product.price,
                        quantity: quantity,
                        image: product.image,
                        shipping: product.shipping,
                      }],
                      total: totalPrice,
                    },
                  });
                }}
              >
                Order Now
              </button>
            </div>

            <div className="product-detail__info-boxes">
              <div className="product-detail__info-card">
                <FontAwesomeIcon icon={faTruck} className="info-icon" />
                <p>Shipping charges will be calculated at the time of checkout</p>
              </div>
              <div className="product-detail__info-card">
                <FontAwesomeIcon icon={faBan} className="info-icon" />
                <p>Cash on Delivery (COD) is not available with us</p>
              </div>
              <div className="product-detail__info-card">
                <FontAwesomeIcon icon={faClock} className="info-icon" />
                <p>Delivery time 7-10 Working days</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}